<script>
import {
    environment
} from '@app/store/envStore.svelte.js';
import {
    modalState, showModal, hideModal
} from '@app/store/modalStore.svelte.js';
import {
    explorerState
} from '@app/store/explorerStore.svelte.js';
import {
    fade
} from 'svelte/transition';
/* Firebase */
import db from '@app/configs/firestore.js';
import Swal from 'sweetalert2';
import { SWAL_BASE, SWAL_CLASSES } from '@app/lib/swalClasses.js';
import * as m from '@app/paraglide/messages.js';

let updateAvailable = $state(false);
let updateSkipped = $state(false);
let remindLater = $state(false);
let newVersion = $state("");

// Check if a specific version was skipped (stored in localStorage)
function isVersionSkipped(version) {
    try {
        const skippedVersion = localStorage.getItem('ccc_skipped_update_version');
        return skippedVersion === version;
    } catch (e) {
        return false;
    }
}

// Save skipped version to localStorage
function skipVersion(version) {
    try {
        localStorage.setItem('ccc_skipped_update_version', version);
        updateSkipped = true;
    } catch (e) {
        console.warn('Could not save skipped version to localStorage:', e);
        updateSkipped = true;
    }
}

function showUpdateAlert() {
    Swal.fire({
        ...SWAL_BASE,
        customClass: {
            confirmButton: 'btn btn-primary me-2 fw-bold',
            denyButton: 'btn btn-outline-secondary mx-2',
            cancelButton: 'btn btn-danger mx-2',
        },
        title: `<i class="bi bi-stars text-warning"></i> ${m.update_available()}`,
        html: `
            <p>${m.update_available_body()}</p>
            <p class="text-muted small m-0">${m.current_version({ version: environment.version })}</p>
            <p class="text-muted small">${m.latest_version({ version: newVersion })}</p>
            <p><a href="https://www.closedcaptioncreator.com/releases.html" target="_blank">${m.release_notes()}</a></p>
        `,
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: `<i class="bi bi-download"></i> ${m.update_now()}`,
        denyButtonText: `<i class="bi bi-clock"></i> ${m.remind_me_later()}`,
        cancelButtonText: `<i class="bi bi-x-lg"></i> ${m.skip_update()}`,
        reverseButtons: false,
        focusConfirm: true,
    }).then((result) => {
        if (result.isConfirmed) {
            // Update Now - open download page
            window.open('https://www.closedcaptioncreator.com/downloads.html', '_blank');
        } else if (result.isDenied) {
            // Remind Me Later - will show again on next session
            remindLater = true;
        } else if (result.dismiss === Swal.DismissReason.cancel) {
            // Skip Update - save to localStorage so it won't show again for this version
            skipVersion(newVersion);
        }
    });
}

if (environment.online) {
    db.collection("system").doc("creator").get().then(doc => {
        if (doc.exists) {
            let data = doc.data();
            updateAvailable = data.version !== environment.version;
            newVersion = data.version;
            
            // Check if this specific version was previously skipped
            updateSkipped = isVersionSkipped(newVersion);
            
            // Show the update alert if an update is available and not skipped
            if (updateAvailable && !updateSkipped && !remindLater) {
                // Small delay to ensure modal is rendered first
                setTimeout(() => {
                    showUpdateAlert();
                }, 500);
            }
        }
    });
}

function openProjectModal() {
    if (environment.online){
        explorerState.context = "load";
        showModal("storageExplorer");
    } else {
        showModal("projectImport");
    }    
}
</script>

<div 
    transition:fade={{duration: 100}} 
    class="modal {modalState.selected === 'welcome' ? 'show d-block' : ''}" 
    role="dialog" 
    tabindex="-1" 
    id="WelcomeModal" 
    aria-labelledby="welcomeModalTitle"
>
    <div class="modal-dialog modal-xl modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h3 class="modal-title" id="welcomeModalTitle">
                    <i class="bi bi-star-fill" aria-hidden="true"></i> {m.welcome()}
                </h3>
                <button type="button" class="btn-close" id="welcomeModalClose" aria-label={m.close()} onclick={hideModal}></button>
            </div>
            <div class="modal-body">
                <div class="container">
                    <div class="row g-0 gx-5">
                        <div class="p-3 {environment.online ? 'col-3' : 'col-6'}">
                            <button 
                                id="newProjectBtn"
                                onclick={() => showModal("newProject")} 
                                type="button" 
                                class="btn btn-light btn-lg w-100 p-4 welcome-option shadow-sm">
                                <i class="bi bi-file-earmark-plus option-icon" aria-hidden="true"></i><hr/>
                                <h4 class="mt-2">{m.new_project()}</h4>
                            </button>
                        </div>
                        <div class="p-3 {environment.online ? 'col-3' : 'col-6'}">
                            <button onclick={openProjectModal} type="button" class="btn btn-light btn-lg w-100 p-4  welcome-option shadow-sm" aria-label={m.open_project()}>
                                <i class="bi bi-folder2 option-icon"></i><hr/>
                                <h4 class="mt-2">{m.open_project()}</h4>
                            </button>
                        </div>
                        {#if environment.online}
                            <div class="col-3 p-3">
                                <button onclick={() => showModal("recentProjects")} type="button" class="btn btn-light btn-lg w-100 p-4  welcome-option shadow-sm" aria-label={m.recent_projects()}>
                                    <i class="bi bi-clock-history option-icon"></i><hr/>
                                    <h4 class="mt-2">{m.recent_projects()}</h4>
                                </button>
                            </div>
                            <div class="col-3 p-3">
                                <button onclick={() => showModal("teamProjectImport")} type="button" class="btn btn-light btn-lg w-100 p-4  welcome-option shadow-sm" aria-label={m.team_project()}>
                                    <i class="bi bi-people option-icon"></i><hr/>
                                    <h4 class="mt-2">{m.team_project()}</h4>
                                </button>
                            </div>
                            <div class="col-3 p-3">
                                <a href="https://www.closedcaptioncreator.com/docs/" target="_blank" rel="noreferrer" class="btn btn-light btn-lg w-100 p-4 welcome-option shadow-sm" aria-label={m.user_guide()}>
                                    <i class="bi bi-journals option-icon"></i><hr/>
                                    <h4 class="mt-2">{m.user_guide()}</h4>
                                </a>
                            </div>
                            <div class="col-3 p-3">
                                <a href="https://www.closedcaptioncreator.com/videos.html" target="_blank" rel="noreferrer" class="btn btn-light btn-lg w-100 p-4 welcome-option shadow-sm" aria-label={m.tutorial_videos()}>
                                    <i class="bi bi-film option-icon"></i><hr/>
                                    <h4 class="mt-2">{m.tutorial_videos()}</h4>
                                </a>
                            </div>
                            <div class="col-3 p-3">
                                <a href="https://www.closedcaptioncreator.com/support.html" target="_blank" rel="noreferrer" class="btn btn-light btn-lg w-100 p-4 welcome-option shadow-sm" aria-label={m.contact_support()}>
                                    <i class="bi bi-life-preserver option-icon"></i><hr/>
                                    <h4 class="mt-2">{m.contact_support()}</h4>
                                </a>
                            </div>
                            <div class="col-3 p-3">
                                <a href="https://billing.stripe.com/p/login/eVqaEX0YyczidOadSsfQI00" target="_blank" rel="noreferrer" class="btn btn-light btn-lg w-100 p-4 welcome-option shadow-sm" aria-label={m.manage_account()}>
                                    <i class="bi bi-award option-icon"></i><hr/>
                                    <h4 class="mt-2">{m.manage_account()}</h4>
                                </a>
                            </div>
                        {/if}
                    </div>
                </div>
            </div>
            <div class="modal-footer" aria-live="polite">
                <p>{m.version_label({ version: environment.version })}</p>                
            </div>
        </div>
    </div>
</div>

<style lang="css">
    .modal-content {
        border: none;
        border-radius: 1rem;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
        backdrop-filter: blur(10px);
        background-color: var(--bs-body-bg);
    }
    
    .modal-header {
        border-radius: 1rem 1rem 0 0;
        padding: 1.5rem 2rem 1rem;
    }
    
    .modal-title {
        font-weight: 600;
        color: var(--bs-body-color);
        font-size: 1.75rem;
    }
    
    .modal-body {
        background: linear-gradient(180deg, var(--bs-body-bg) 0%, var(--bs-light-bg-subtle) 100%);
        padding: 2rem;
    }
    
    .modal-footer {
        background: linear-gradient(0deg, var(--bs-body-bg) 0%, var(--bs-light-bg-subtle) 100%);
        border-radius: 0 0 1rem 1rem;
        padding: 1.5rem 2rem;
    }
    
    .welcome-option {
        transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        background: linear-gradient(135deg, #464646d4 0%, var(--bs-light-bg-subtle) 100%);
        border-radius: 1rem !important;
        border-color: rgb(22, 22, 22);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08) !important;
        position: relative;
        overflow: hidden;
        backdrop-filter: blur(10px);
    }
    
    .welcome-option::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: var(--bs-primary);
        opacity: 0;
        transition: opacity 0.3s ease;
        z-index: 0;
    }
    
    .welcome-option > * {
        position: relative;
        z-index: 1;
    }
    
    .welcome-option:hover {
        transform: translateY(-8px) scale(1.02);
        background-color: transparent !important;
        color: inherit !important;
    }
    
    .welcome-option:hover::before {
        opacity: 1;
    }
    
    .welcome-option:hover * {
        color: white !important;
        transform: scale(1.05);
    }
    
    .welcome-option:hover .option-icon {
        transform: scale(1.1);
    }
    
    .welcome-option:focus {
        outline: none;
        box-shadow: 0 0 0 3px rgba(var(--bs-primary-rgb), 0.3) !important;
        transform: translateY(-4px);
    }
    
    .welcome-option:active {
        transform: translateY(-2px) scale(0.98);
    }
    
    .option-icon {
        margin-top: 2rem;
        margin-bottom: 1rem;
        font-size: 5rem;
        color: var(--bs-primary);
        transition: all 0.3s ease;
        display: block;
    }
    
    .welcome-option h4 {
        font-weight: 600;
        color: var(--bs-body-color);
        margin: 0;
        transition: all 0.3s ease;
        font-size: 1.1rem;
    }
    
    .welcome-option hr {
        border: none;
        height: 2px;
        background: linear-gradient(90deg, transparent 0%, var(--bs-primary) 50%, transparent 100%);
        margin: 1rem 0;
        opacity: 0.3;
        transition: all 0.3s ease;
    }
    
    .welcome-option:hover hr {
        background: linear-gradient(90deg, transparent 0%, white 50%, transparent 100%);
        opacity: 0.8;
    }
    
    /* Version text styling */
    .modal-footer p {
        color: var(--bs-secondary-color);
        margin: 0;
        font-weight: 500;
    }
    
    /* Responsive improvements */
    @media (max-width: 1200px) {
        .option-icon {
            font-size: 2.5rem;
            margin-top: 2rem;
        }
        
        .welcome-option h4 {
            font-size: 1rem;
        }
        
        .welcome-option {
            padding: 2rem 1rem !important;
        }
    }
    
    @media (max-width: 768px) {
        .modal-header {
            padding: 1rem 1.5rem 0.75rem;
        }
        
        .modal-title {
            font-size: 1.5rem;
        }
        
        .modal-body {
            padding: 1rem;
        }
        
        .option-icon {
            font-size: 2rem;
            margin-top: 1.5rem;
        }
        
        .welcome-option h4 {
            font-size: 0.9rem;
        }
        
        .welcome-option {
            padding: 1.5rem 0.75rem !important;
        }
    }
</style>
